import React, { useEffect, useRef, useState } from 'react'
import { Form, Button, Modal, Spinner } from 'react-bootstrap'
import { AddressElement, TaxIdElement } from '@stripe/react-stripe-js'
import CustomModal from 'components/CustomModal/CustomModal'
import styles from '../../PaymentModal.module.css'
import './BusinessDetailsModal.css'

const getInitialAddress = (initialValues = {}) => initialValues.address?.address || initialValues.address || {}

const hasRequiredAddressFields = (address = {}) =>
  Boolean(address.line1 && address.city && address.postal_code && address.country)

const BusinessDetailsModal = ({ show, onHide, onSave, initialValues = {} }) => {
  const [addressComplete, setAddressComplete] = useState(false)
  const [addressError, setAddressError] = useState(null)
  const [taxIdComplete, setTaxIdComplete] = useState(true)
  const [taxIdError, setTaxIdError] = useState(null)
  const [addressOptions, setAddressOptions] = useState(null)
  const [taxIdOptions, setTaxIdOptions] = useState(null)
  const [elementsReady, setElementsReady] = useState({
    address: false,
    taxId: false
  })
  const addressElementRef = useRef(null)
  const taxIdElementRef = useRef(null)
  const isElementsLoading = show && (!elementsReady.address || !elementsReady.taxId)

  useEffect(() => {
    if (show) {
      const initialAddress = getInitialAddress(initialValues)
      const organization = initialValues.organization || initialValues.address?.name || ''
      const defaultAddress = {
        ...initialAddress,
        country: initialAddress.country || 'US'
      }

      setAddressError(null)
      setTaxIdError(null)
      setAddressComplete(hasRequiredAddressFields(initialAddress))
      setTaxIdComplete(true)
      setElementsReady({
        address: false,
        taxId: false
      })

      setAddressOptions({
        mode: 'billing',
        fields: {
          phone: 'never'
        },
        display: {
          name: 'organization'
        },
        defaultValues: {
          ...(organization ? { name: organization } : {}),
          address: defaultAddress
        }
      })

      setTaxIdOptions({
        visibility: 'always',
        fields: {
          businessName: 'never'
        },
        validation: {
          taxId: {
            required: 'never'
          }
        },
        defaultValues: {
          ...(organization ? { businessName: organization } : {}),
          ...(initialValues.vatId ? { taxId: initialValues.vatId } : {}),
          ...(initialValues.taxIdType ? { taxIdType: initialValues.taxIdType } : {})
        }
      })

      addressElementRef.current = null
      taxIdElementRef.current = null
    }
  }, [show, initialValues])

  const handleAddressChange = (event) => {
    setAddressComplete(event.complete)
    if (event.error) {
      setAddressError(event.error.message)
    } else {
      setAddressError(null)
    }
  }

  const handleTaxIdChange = (event) => {
    setTaxIdComplete(event.empty || event.complete)
    if (event.error) {
      setTaxIdError(event.error.message)
    } else {
      setTaxIdError(null)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    event.stopPropagation()

    try {
      setAddressError(null)
      setTaxIdError(null)

      if (!addressElementRef.current) {
        setAddressError('Address element not initialized')
        return
      }

      const addressValue = await addressElementRef.current.getValue()
      const taxIdValue = taxIdElementRef.current ? await taxIdElementRef.current.getValue() : null

      if (!addressValue.complete) {
        setAddressError('Please complete your billing address')
        return
      }

      if (taxIdValue && !taxIdValue.empty && !taxIdValue.complete) {
        setTaxIdError('Please complete your tax ID or clear it before saving')
        return
      }

      const organization = addressValue.value?.name?.trim() || taxIdValue?.value?.businessName?.trim()

      if (!organization) {
        setAddressError('Business name is required')
        return
      }

      const businessDetails = {
        organization,
        address: addressValue.value.address,
        isBusinessAccount: true
      }

      if (taxIdValue && !taxIdValue.empty && taxIdValue.value?.taxId?.trim()) {
        businessDetails.vatId = taxIdValue.value.taxId.trim()
        businessDetails.taxIdType = taxIdValue.value.taxIdType
        businessDetails.externalTaxIdType = taxIdValue.value.externalTaxIdType
      }

      onSave(businessDetails)
    } catch (error) {
      console.error('Error getting business details value:', error)
      setAddressError('Error processing business details')
    }
  }

  return (
    <CustomModal show={show} onHide={onHide} aria-labelledby="contained-modal-title-vcenter" centered>
      <div className="p-2">
        <Modal.Header closeButton>
          <Modal.Title>Business details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            {isElementsLoading && (
              <div className="business-details-loading mb-3">
                <Spinner animation="border" size="sm" role="status" className="me-2" />
                <span>Loading secure Stripe fields...</span>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                Business name and billing address <span className="text-danger">*</span>
              </Form.Label>
              {addressOptions && (
                <div className="business-details-element-slot business-details-address-slot">
                  <div
                    className={
                      isElementsLoading
                        ? 'business-details-element business-details-element--hidden'
                        : 'business-details-element'
                    }
                  >
                    <div className="address-element-container">
                      <AddressElement
                        options={addressOptions}
                        className={`${styles.StripeElement} business-details-stripe-element`}
                        onChange={handleAddressChange}
                        onReady={(element) => {
                          addressElementRef.current = element
                          setElementsReady((prevState) => ({
                            ...prevState,
                            address: true
                          }))
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {addressError && <div className="validation-error mt-2">{addressError}</div>}
              <Form.Text className="text-muted">
                Your business name and invoice address are collected by Stripe.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                VAT ID / Tax ID <span className="text-muted">(optional)</span>
              </Form.Label>
              {taxIdOptions && (
                <div className="business-details-element-slot business-details-tax-slot">
                  <div
                    className={
                      isElementsLoading
                        ? 'business-details-element business-details-element--hidden'
                        : 'business-details-element'
                    }
                  >
                    <div className="address-element-container">
                      <TaxIdElement
                        options={taxIdOptions}
                        className={`${styles.StripeElement} business-details-stripe-element`}
                        onChange={handleTaxIdChange}
                        onReady={(element) => {
                          taxIdElementRef.current = element
                          setElementsReady((prevState) => ({
                            ...prevState,
                            taxId: true
                          }))
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {taxIdError && <div className="validation-error mt-2">{taxIdError}</div>}
              <Form.Text className="text-muted">Add a tax ID if you want it included on Stripe invoices.</Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
              <Button variant="outline-secondary" className="me-2" type="button" onClick={onHide}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isElementsLoading || !addressComplete || !taxIdComplete}
              >
                Save
              </Button>
            </div>
          </form>
        </Modal.Body>
      </div>
    </CustomModal>
  )
}

export default BusinessDetailsModal
